import * as React from 'react';
import classNames from 'classnames';
import Icon from '../../icon';
import { TreeContext } from './contextTypes';
import { getDataAndAria } from './util';
import Indent from './Indent';
import { convertNodePropsToEventData } from './utils/treeUtil';
import { stopEvent } from '../../_util/EventManager';
import Ripple from '../../ripple';
const ICON_OPEN = 'open';
const ICON_CLOSE = 'close';
const defaultTitle = '---';
class InternalTreeNode extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            dragNodeHighlight: false,
        };
        this.onSelectorClick = (e) => {
            // Click trigger before select/check operation
            const { context, } = this.props;
            const { onNodeClick } = context;
            onNodeClick(e, convertNodePropsToEventData(this.props));
            if (this.isSelectable()) {
                this.onSelect(e);
            }
            else {
                this.onCheck(e);
            }
        };
        this.onSelectorDoubleClick = (e) => {
            const { context, } = this.props;
            const { onNodeDoubleClick } = context;
            onNodeDoubleClick(e, convertNodePropsToEventData(this.props));
        };
        this.onSelect = (e) => {
            if (this.isDisabled())
                return;
            const { context, } = this.props;
            const { onNodeSelect } = context;
            e.preventDefault();
            onNodeSelect(e, convertNodePropsToEventData(this.props));
        };
        this.onCheck = (e) => {
            if (this.isDisabled())
                return;
            const { disableCheckbox } = this.props;
            if (!this.isCheckable() || disableCheckbox)
                return;
            const { checked, context } = this.props;
            const { onNodeCheck } = context;
            stopEvent(e);
            const targetChecked = !checked;
            onNodeCheck(e, convertNodePropsToEventData(this.props), targetChecked);
        };
        this.onMouseEnter = (e) => {
            const { context, } = this.props;
            const { onNodeMouseEnter } = context;
            onNodeMouseEnter(e, convertNodePropsToEventData(this.props));
        };
        this.onMouseLeave = (e) => {
            const { context, } = this.props;
            const { onNodeMouseLeave } = context;
            onNodeMouseLeave(e, convertNodePropsToEventData(this.props));
        };
        this.onContextMenu = (e) => {
            const { context, } = this.props;
            const { onNodeContextMenu } = context;
            onNodeContextMenu(e, convertNodePropsToEventData(this.props));
        };
        this.onDragStart = (e) => {
            const { context, } = this.props;
            const { onNodeDragStart } = context;
            e.stopPropagation();
            this.setState({
                dragNodeHighlight: true,
            });
            onNodeDragStart(e, this);
            try {
                // ie throw error
                // firefox-need-it
                e.dataTransfer.setData('text/plain', '');
            }
            catch (error) {
                // empty
            }
        };
        this.onDragEnter = (e) => {
            const { context, } = this.props;
            const { onNodeDragEnter } = context;
            e.preventDefault();
            e.stopPropagation();
            onNodeDragEnter(e, this);
        };
        this.onDragOver = (e) => {
            const { context, } = this.props;
            const { onNodeDragOver } = context;
            e.preventDefault();
            e.stopPropagation();
            onNodeDragOver(e, this);
        };
        this.onDragLeave = (e) => {
            const { context, } = this.props;
            const { onNodeDragLeave } = context;
            e.stopPropagation();
            onNodeDragLeave(e, this);
        };
        this.onDragEnd = (e) => {
            const { context, } = this.props;
            const { onNodeDragEnd } = context;
            e.stopPropagation();
            this.setState({
                dragNodeHighlight: false,
            });
            onNodeDragEnd(e, this);
        };
        this.onDrop = (e) => {
            const { context, } = this.props;
            const { onNodeDrop } = context;
            e.preventDefault();
            e.stopPropagation();
            this.setState({
                dragNodeHighlight: false,
            });
            onNodeDrop(e, this);
        };
        // Disabled item still can be switch
        this.onExpand = e => {
            stopEvent(e);
            const { loading, context, } = this.props;
            const { onNodeExpand } = context;
            if (loading)
                return;
            onNodeExpand(e, convertNodePropsToEventData(this.props));
        };
        // Drag usage
        this.setSelectHandle = node => {
            this.selectHandle = node;
        };
        this.getNodeState = () => {
            const { expanded } = this.props;
            if (this.isLeaf()) {
                return null;
            }
            return expanded ? ICON_OPEN : ICON_CLOSE;
        };
        this.hasChildren = () => {
            const { eventKey, context } = this.props;
            const { keyEntities, } = context;
            const { children } = keyEntities[eventKey] || {};
            return !!(children || []).length;
        };
        this.isLeaf = () => {
            const { isLeaf, loaded, context } = this.props;
            const { loadData, } = context;
            const hasChildren = this.hasChildren();
            if (isLeaf === false) {
                return false;
            }
            return isLeaf || (!loadData && !hasChildren) || (loadData && loaded && !hasChildren);
        };
        this.isDisabled = () => {
            const { disabled, context } = this.props;
            const { disabled: treeDisabled, } = context;
            return !!(treeDisabled || disabled);
        };
        this.isCheckable = () => {
            const { checkable, context } = this.props;
            const { checkable: treeCheckable, } = context;
            // Return false if tree or treeNode is not checkable
            if (!treeCheckable || checkable === false)
                return false;
            return treeCheckable;
        };
        // Load data to avoid default expanded tree without data
        this.syncLoadData = props => {
            const { expanded, loading, loaded, context } = props;
            const { loadData, onNodeLoad, } = context;
            if (loading)
                return;
            // read from state to avoid loadData at same time
            if (loadData && expanded && !this.isLeaf()) {
                // We needn't reload data when has children in sync logic
                // It's only needed in node expanded
                if (!this.hasChildren() && !loaded) {
                    onNodeLoad(convertNodePropsToEventData(this.props));
                }
            }
        };
        this.getMergedDraggable = (draggable, data) => {
            if (typeof draggable === 'function') {
                return draggable(data);
            }
            if (typeof draggable === 'object') {
                const { nodeDraggable = false } = draggable;
                return typeof nodeDraggable === 'function'
                    ? nodeDraggable(data)
                    : nodeDraggable;
            }
            return draggable;
        };
        // renderDraggableIcon
        this.renderDraggableIcon = () => {
            const { context } = this.props;
            const { prefixCls, draggable, } = context;
            if (typeof draggable === 'object') {
                const { icon = false } = draggable;
                if (icon) {
                    return (React.createElement("span", { className: `${prefixCls}-draggable-icon` }, React.isValidElement(icon) ? icon : React.createElement(Icon, { type: "baseline-drag_indicator" })));
                }
                return null;
            }
        };
        // Switcher
        this.renderSwitcher = () => {
            const { expanded, switcherIcon: switcherIconFromProps, context } = this.props;
            const { prefixCls, switcherIcon: switcherIconFromCtx, } = context;
            const switcherIcon = switcherIconFromProps || switcherIconFromCtx;
            if (this.isLeaf()) {
                return (React.createElement("span", { className: classNames(`${prefixCls}-switcher`, `${prefixCls}-switcher-noop`) }, typeof switcherIcon === 'function'
                    ? switcherIcon({ ...this.props, isLeaf: true })
                    : switcherIcon));
            }
            const switcherCls = classNames(`${prefixCls}-switcher`, `${prefixCls}-switcher_${expanded ? ICON_OPEN : ICON_CLOSE}`);
            return (React.createElement("span", { onClick: this.onExpand, className: switcherCls }, typeof switcherIcon === 'function'
                ? switcherIcon({ ...this.props, isLeaf: false })
                : switcherIcon));
        };
        // Checkbox
        this.renderCheckbox = () => {
            const { checked, halfChecked, disableCheckbox, context } = this.props;
            const { prefixCls, } = context;
            const disabled = this.isDisabled();
            const checkable = this.isCheckable();
            if (!checkable)
                return null;
            // [Legacy] Custom element should be separate with `checkable` in future
            const $custom = typeof checkable !== 'boolean' ? checkable : null;
            return (React.createElement("span", { className: classNames(`${prefixCls}-checkbox`, checked && `${prefixCls}-checkbox-checked`, !checked && halfChecked && `${prefixCls}-checkbox-indeterminate`, (disabled || disableCheckbox) && `${prefixCls}-checkbox-disabled`), onClick: this.onCheck }, $custom));
        };
        this.renderIcon = () => {
            const { loading, context } = this.props;
            const { prefixCls, } = context;
            return (React.createElement("span", { className: classNames(`${prefixCls}-iconEle`, `${prefixCls}-icon__${this.getNodeState() || 'docu'}`, loading && `${prefixCls}-icon_loading`) }));
        };
        // Icon + Title
        this.renderSelector = () => {
            const { dragNodeHighlight } = this.state;
            const { title, selected, icon, loading, data, context } = this.props;
            const { prefixCls, showIcon, icon: treeIcon, draggable, loadData, titleRender, } = context;
            const disabled = this.isDisabled();
            const mergedDraggable = this.getMergedDraggable(draggable, data);
            const wrapClass = `${prefixCls}-node-content-wrapper`;
            // Icon - Still show loading icon when loading without showIcon
            let $icon;
            if (showIcon) {
                const currentIcon = icon || treeIcon;
                $icon = currentIcon ? (React.createElement("span", { className: classNames(`${prefixCls}-iconEle`, `${prefixCls}-icon__customize`) }, typeof currentIcon === 'function' ? currentIcon(this.props) : currentIcon)) : (this.renderIcon());
            }
            else if (loadData && loading) {
                $icon = this.renderIcon();
            }
            // Title
            let titleNode;
            if (typeof title === 'function') {
                titleNode = title(data);
            }
            else if (titleRender) {
                titleNode = titleRender(data);
            }
            else {
                titleNode = title;
            }
            const $title = React.createElement("span", { className: `${prefixCls}-title` }, titleNode);
            return (React.createElement("span", { ref: this.setSelectHandle, title: typeof title === 'string' ? title : '', className: classNames(`${wrapClass}`, `${wrapClass}-${this.getNodeState() || 'normal'}`, !disabled && (selected || dragNodeHighlight) && `${prefixCls}-node-selected`, !disabled && mergedDraggable && 'draggable'), onMouseEnter: this.onMouseEnter, onMouseLeave: this.onMouseLeave, onContextMenu: this.onContextMenu },
                $icon,
                $title,
                this.renderDropIndicator()));
        };
        this.renderDropIndicator = () => {
            const { disabled, eventKey, context } = this.props;
            const { draggable, dropLevelOffset, dropPosition, prefixCls, indent, dropIndicatorRender, dragOverNodeKey, direction, } = context;
            let isDraggable = draggable;
            if (typeof draggable === 'object') {
                const { nodeDraggable = false } = draggable;
                isDraggable = nodeDraggable;
            }
            const mergedDraggable = isDraggable !== false;
            // allowDrop is calculated in Tree.tsx, there is no need for calc it here
            const showIndicator = !disabled && mergedDraggable && dragOverNodeKey === eventKey;
            return showIndicator && dropIndicatorRender
                ? dropIndicatorRender({ dropPosition, dropLevelOffset, indent, prefixCls, direction })
                : null;
        };
    }
    // Isomorphic needn't load data in server side
    componentDidMount() {
        this.syncLoadData(this.props);
    }
    componentDidUpdate() {
        this.syncLoadData(this.props);
    }
    isSelectable() {
        const { selectable, context } = this.props;
        const { selectable: treeSelectable, } = context;
        // Ignore when selectable is undefined or null
        if (typeof selectable === 'boolean') {
            return selectable;
        }
        return treeSelectable;
    }
    render() {
        const { eventKey, className, style, dragOver, dragOverGapTop, dragOverGapBottom, isLeaf, isStart = [], isEnd = [], expanded, selected, checked, halfChecked, loading, domRef, active, data, onMouseMove, context, ...otherProps } = this.props;
        const { prefixCls, filterTreeNode, draggable, keyEntities, dropContainerKey, dropTargetKey, ripple, } = context;
        const disabled = this.isDisabled();
        const dataOrAriaAttributeProps = getDataAndAria(otherProps);
        const { level } = keyEntities[eventKey] || {};
        const isEndNode = isEnd[isEnd.length - 1];
        const mergedDraggable = this.getMergedDraggable(draggable, data);
        const draggableWithoutDisabled = !disabled && mergedDraggable;
        return (React.createElement(React.Fragment, null,
            React.createElement(Ripple, { disabled: disabled || !ripple || !this.isSelectable() },
                React.createElement("div", Object.assign({ ref: domRef, className: classNames(className, `${prefixCls}-treenode`, {
                        [`${prefixCls}-treenode-disabled`]: disabled,
                        [`${prefixCls}-treenode-switcher-${expanded ? 'open' : 'close'}`]: !isLeaf,
                        [`${prefixCls}-treenode-checkbox-checked`]: checked,
                        [`${prefixCls}-treenode-checkbox-indeterminate`]: halfChecked,
                        [`${prefixCls}-treenode-selected`]: selected,
                        [`${prefixCls}-treenode-loading`]: loading,
                        [`${prefixCls}-treenode-active`]: active,
                        [`${prefixCls}-treenode-leaf-last`]: isEndNode,
                        'drop-target': dropTargetKey === eventKey,
                        'drop-container': dropContainerKey === eventKey,
                        'drag-over': !disabled && dragOver,
                        'drag-over-gap-top': !disabled && dragOverGapTop,
                        'drag-over-gap-bottom': !disabled && dragOverGapBottom,
                        'filter-node': filterTreeNode && filterTreeNode(convertNodePropsToEventData(this.props)),
                    }), style: style, draggable: draggableWithoutDisabled, onDragStart: draggableWithoutDisabled ? this.onDragStart : undefined, onClick: this.onSelectorClick, onDoubleClick: this.onSelectorDoubleClick, onDragEnter: mergedDraggable ? this.onDragEnter : undefined, onDragOver: mergedDraggable ? this.onDragOver : undefined, onDragLeave: mergedDraggable ? this.onDragLeave : undefined, onDrop: mergedDraggable ? this.onDrop : undefined, onDragEnd: mergedDraggable ? this.onDragEnd : undefined, onMouseMove: onMouseMove }, dataOrAriaAttributeProps),
                    React.createElement(Indent, { prefixCls: prefixCls, level: level, isStart: isStart, isEnd: isEnd }),
                    this.renderDraggableIcon(),
                    this.renderSwitcher(),
                    this.renderCheckbox(),
                    this.renderSelector()))));
    }
}
const ContextTreeNode = props => (React.createElement(TreeContext.Consumer, null, context => React.createElement(InternalTreeNode, Object.assign({}, props, { context: context }))));
ContextTreeNode.displayName = 'TreeNode';
ContextTreeNode.defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    title: defaultTitle,
};
ContextTreeNode.isTreeNode = 1;
export { InternalTreeNode };
export default ContextTreeNode;
